// Folds the per-edit `accepts_nested_attributes_for` bodies a form used to send
// one at a time into a single body it can send once, on Save.
//
// The rules follow how Rails reads such a body. A scalar is its latest value.
// A has_one block (`*_attributes: {}`) merges field by field, except that a
// `_destroy` replaces whatever was staged before it, and vice versa: deleting
// and then re-adding are not the same record. A has_many list
// (`*_attributes: []`) is keyed by record: one the server already knows carries
// `id`, one created on this page carries `_localId` until it is saved, and an
// entry for a record already in the list overrides that record's fields.
//
// `_destroy: false` withdraws every pending change for that record - the
// merchant took the delete back - and a destroyed record the server has never
// seen is simply forgotten rather than sent.

const LOCAL_ID_PREFIX = 'local-'
let localIdCounter = 0

export const localId = () => `${LOCAL_ID_PREFIX}${++localIdCounter}`

export const isLocalId = id => typeof id === 'string' && id.startsWith(LOCAL_ID_PREFIX)

const isPlainObject = value => value !== null && typeof value === 'object' && !Array.isArray(value)

const isEmpty = value =>
  (Array.isArray(value) && value.length === 0) || (isPlainObject(value) && Object.keys(value).length === 0)

// A record's identity within a has_many list.
export const entryKey = entry => (isPlainObject(entry) ? entry.id ?? entry._localId ?? null : null)

const sameKey = (a, b) => a !== null && b !== null && String(a) === String(b)

const mergeEntries = (base, changes) => {
  const list = [...base]
  for (const entry of changes) {
    const key = entryKey(entry)
    const index = list.findIndex(existing => sameKey(entryKey(existing), key))
    if (index === -1) {
      if (entry._destroy !== false) list.push(entry)
      continue
    }
    const withdrawn = entry._destroy === false || (entry._destroy && isLocalId(key))
    if (withdrawn) list.splice(index, 1)
    else list[index] = { ...list[index], ...entry }
  }
  return list
}

export const mergeNestedAttributes = (base = {}, change = {}) => {
  const result = { ...base }
  for (const [key, value] of Object.entries(change)) {
    const existing = base[key]
    if (Array.isArray(value)) {
      result[key] = mergeEntries(Array.isArray(existing) ? existing : [], value)
    } else if (isPlainObject(value)) {
      if (value._destroy === false) {
        delete result[key]
        continue
      }
      const replace = Boolean(value._destroy) || !isPlainObject(existing) || Boolean(existing._destroy)
      result[key] = replace ? value : mergeNestedAttributes(existing, value)
    } else {
      result[key] = value
    }
    if (isEmpty(result[key])) delete result[key]
  }
  return result
}

// The body as the server should see it: local ids are this page's business.
export const serializeNestedAttributes = value => {
  if (Array.isArray(value)) return value.map(serializeNestedAttributes)
  if (!isPlainObject(value)) return value
  const out = {}
  for (const [key, nested] of Object.entries(value)) {
    if (key !== '_localId') out[key] = serializeNestedAttributes(nested)
  }
  return out
}
